// src/utils/labStorage.ts

/**
 * Laboratory local-storage keys.
 *
 * TODO:
 * Remove this persistence layer when the laboratory backend API
 * becomes the source of truth.
 */
export const LAB_STORAGE_KEYS = {
  TEST_REQUESTS: "lab_test_requests",
  ONGOING_TESTS: "lab_ongoing_tests",
  COMPLETED_TESTS: "lab_completed_tests",
  PATIENTS: "lab_patients",
} as const;

export type LabStorageKey =
  (typeof LAB_STORAGE_KEYS)[keyof typeof LAB_STORAGE_KEYS];

/**
 * Safely loads and parses a value from localStorage.
 *
 * Returns the provided fallback when:
 * - the key does not exist;
 * - the stored JSON is invalid;
 * - localStorage is unavailable;
 * - the code is rendered outside the browser.
 */
export const loadLabStorage = <T,>(
  key: LabStorageKey,
  fallbackValue: T,
): T => { return fallbackValue; };

/**
 * Safely serializes and stores a value in localStorage.
 */
export const saveLabStorage = <T,>(
  key: LabStorageKey,
  value: T,
): void => { /* Business records require backend persistence. */ };

/**
 * Removes one laboratory value from localStorage.
 */
export const removeLabStorage = (
  key: LabStorageKey,
): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.removeItem(key);
  } catch {
    // TODO:
    // Send storage failures to centralized error monitoring.
  }
};

/**
 * Removes all laboratory-related values from localStorage.
 */
export const clearLabStorage = (): void => {
  if (typeof window === "undefined") {
    return;
  }

  Object.values(LAB_STORAGE_KEYS).forEach(
    (key) => {
      removeLabStorage(key);
    },
  );
};